import { Editor } from 'grapesjs';
import { Quill } from 'quill';
import { getQuillFontName } from '@loyalty/quill';
import { EditorContext } from './editor-service';
import { getHeaderDropdownCss, getToolbarStylesCss } from './rte-styles';
import { getThemeColors } from '../editor/config';
import { injectQuillFontsStyles } from './quill-fonts';

/**
 * Registers a custom Header Blot that automatically applies CSS classes
 * when selecting heading levels in the toolbar dropdown.
 * This ensures visual consistency with the theme's heading styles.
 */
function registerCustomHeaderBlot(_quilljs: typeof Quill): void {
  const Header = _quilljs.import('formats/header');

  class CustomHeader extends Header {
    static create(value: string | number) {
      const node = super.create(value);

      if (value.toString() === '1') {
        node.setAttribute('class', 'qlfeditor-title');
      } else if (value.toString() === '2') {
        node.setAttribute('class', 'qlfeditor-subtitle');
      }

      return node;
    }
  }

  CustomHeader.blotName = 'header';
  CustomHeader.tagName = Header.tagName;

  _quilljs.register(CustomHeader, true);
}

export const rtePlugin = (editor: Editor) => {
  editor.setCustomRte({
    enable(this: any, el: any, rte: any) {
      if (rte) {
        rte = null;
      }

      const grapesComponentId = el.id;
      const window = editor.Canvas.getWindow();
      const document = window.document;
      const { fonts, theme, t } = editor.getConfig('context') as EditorContext;
      const _quilljs: typeof Quill = (window as any).Quill;
      let fontNames: string[] = [];

      // Register custom header blot with CSS class mapping
      registerCustomHeaderBlot(_quilljs);

      if (fonts?.length) {
        // https://stackoverflow.com/a/53487445
        const Font = _quilljs.import('formats/font');
        fontNames = fonts
          .map(f => f.name)
          .map(getQuillFontName)
          .sort();
        Font.whitelist = fontNames;
        _quilljs.register(Font, true);

        injectQuillFontsStyles(editor, fonts);
      }
      /**
       * Quill toolbar module docs: https://v1.quilljs.com/docs/modules/toolbar
       * Available formats:
       *  - v1: https://v1.quilljs.com/docs/formats
       *  - v2: https://quilljs.com/docs/formats (used as inspiration for the toolbar below)
       *
       * To customize the sizes available: https://stackoverflow.com/questions/38623716/how-to-add-custom-font-sizes-to-quilljs-editor
       */
      const toolbarOptions = [
        [{ font: [false, ...fontNames] }], // false is the default font
        [{ header: [1, 2, false] }], // H1 and H2 and Paragraph (false)
        [{ color: ['custom-picker', ...(theme ? getThemeColors(theme) : [])] }],
        ['bold', 'italic', 'underline', 'strike'],
        ['clean'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ indent: '-1' }, { indent: '+1' }],
        [{ align: [] }],
        ['link'],
      ];

      const editorDiv = document.createElement('div');
      editorDiv.innerHTML = el.innerHTML; // quill initializes its content from the innerHTML of the div given as first argument of its constructor

      /*
       * Quill also places its toolbar in the parent of the div given as first argument of its constructor
       * This means if we remove "editorDiv" from the DOM, the toolbar would stay in the page and everytime
       * the rte is enabled again, a new toolbar would be created.
       * Puttin everything in a wrapper div allows us to remove the whole thing (editor + toolbar) when the rte is disabled,
       * and create a new one when rte is enabled again, without having toolbar duplication.
       */
      const editorWrapper = document.createElement('div');
      editorWrapper.id = `quill-${grapesComponentId}`;
      editorWrapper.appendChild(editorDiv);

      // Get translated color picker label (with fallback)
      const colorPickerLabel = t ? t('editor.rte.colorPicker') : 'Color Picker';

      const styles = document.createElement('style');
      styles.textContent = `
        .ql-color .ql-picker-options [data-value='custom-picker'],
        .ql-background .ql-picker-options [data-value='custom-picker'] {
          background: transparent !important;
          width: 100% !important;
          height: auto !important;
          margin-top: 4px !important;
          padding-top: 4px !important;
          border-top: 1px solid #e0e0e0 !important;
        }

        .ql-color .ql-picker-options [data-value='custom-picker']:before,
        .ql-background .ql-picker-options [data-value='custom-picker']:before {
          content: '${colorPickerLabel}';
          display: block;
          font-size: 11px;
          color: #666;
          text-align: center;
          cursor: pointer;
        }

        .ql-color .ql-picker-options [data-value='custom-picker']:hover:before,
        .ql-background .ql-picker-options [data-value='custom-picker']:hover:before {
          color: #333;
        }

        .ql-color-label.ql-stroke.ql-transparent {
          opacity: 1;
        }

        ${t ? getHeaderDropdownCss(t) : ''}

        ${getToolbarStylesCss()}
      `;
      editorWrapper.appendChild(styles);

      // Ensure el will only have Quill's content as its children.
      el.innerHTML = '';
      el.appendChild(editorWrapper);

      rte = new _quilljs(editorDiv, {
        theme: 'snow',
        // scrollContainer: 'div[data-gjs-type=wrapper]',
        modules: {
          toolbar: toolbarOptions,
        },
      });

      // Create a hidden color input in the PARENT document (not iframe)
      // This ensures the native color picker dialog works properly
      const parentDocument = editor.Canvas.getDocument().defaultView?.parent?.document || document;
      const parentWindow = parentDocument.defaultView;
      const colorInput = parentDocument.createElement('input');
      colorInput.type = 'color';
      colorInput.id = `color-picker-${grapesComponentId}`;
      // Position at center of viewport
      colorInput.style.cssText = `
        position: fixed;
        top: 50%;
        left: 50%;
        width: 1px;
        height: 1px;
        padding: 0;
        border: none;
        opacity: 0;
        pointer-events: none;
      `;
      colorInput.value = '#000000';
      parentWindow?.document.body.appendChild(colorInput);

      const currentRteInstance = rte;

      rte.getModule('toolbar').addHandler('color', function (value: string) {
        if (value === 'custom-picker') {
          colorInput.click();
        } else {
          currentRteInstance.format('color', value);
        }
      });

      // Track color changes in real-time (fires as user picks)
      colorInput.addEventListener('input', (e: Event) => {
        currentRteInstance.format('color', (e.target as HTMLInputElement).value);
      });

      rte.destroy = function () {
        colorInput.remove();
        for (const [key] of Object.entries(this)) {
          delete this[key];
        }
        rte.destroyed = true;
      };

      this.focus(el, rte);

      return rte;
    },

    disable(el: any, rte: Quill) {
      // remove entire editor from the GrapesJS canvas
      editor.Canvas.getDocument().getElementById(`quill-${el.id}`)?.remove();

      rte?.blur();

      /*
       * Replace this component's (el) content with Quill's content.
       * We must wrap the content with .ql-snow and .ql-editor classes because when you apply
       * toolbar formats to content in the Quill editor, it wraps your selection with a span/strong/em... with custom classes.
       * Those custom classes are define in the Quill's theme css file, which prefixes all rules with .ql-<theme-name>. In our
       * case, the theme is "snow", so all rules are prefixed with .ql-snow.
       */
      el.innerHTML = `
        <div class="ql-snow">
          <div class="ql-editor">
            ${rte.root.innerHTML}
            </div>
        </div>
      `;
    },

    /*
     * This function is required for overriding the custom RTE of GrapesJS, but we don't really need it as
     * everytime the editor is enabled or disabled, we remove the entire editor from the DOM and create a new one.
     * Hence, there is no need for a focus state.
     */
    focus(_el: any, rte: Quill) {
      if (rte?.hasFocus()) {
        return;
      }
      rte?.focus();
    },
  });
};
